import React from 'react'
import { useState, createContext, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import CheckIcon from "../../consteNew/icons/CheckIcon";
import EditIcon from "../../consteNew/icons/EditIcon";
import { usePlanContext } from "../../consteNew/context/planContext";
import PlansLoadingSkeleton from "../../consteNew/Skeleton/PlansLoadingSkeleton";
import { handleSignUp } from "../../consteNew/utils";
import PlanDetailsModal from "../../consteNew/PlanDetailsModal";
import ZipCodeDialog from "../../consteNew/ZipCodeDialog";
import ZipCodeBanner from "../../consteNew/ZipCodebanner";
import PlansUnavailableFallback from "../../consteNew/PlansUnavailableFallback";
import { getCurrentPromoCode } from "../../../utils/promoCodeUtils";
//importing all style components from components file
import {
    ChoosePlanContainer,
    ChoosePlanTitle,
    PricingPlansWrapper,
    PricingFiltersContainer,
    PricingFiltersWrapper,
    TabButtonGroup,
    TabButton,
    FilterButton,
    PricingCardsGrid,
    PricingCard,
    PricingCardInner,
    PricingCardHeader,
    PricingCardTitle,
    PlanName,
    BestValueBadge,
    BestValueText,
    PricingCardBody,
    PriceDisplay,
    PriceValue,
    PriceUnit,
    SignUpButton,
    FeaturesContainer,
    FeatureItem,
    FeatureText,
    ViewDetailsLink,
    SavingsContainer,
    SavingsBox,
    SavingsLabel,
    SavingsBottom,
    SavingsValue,
    SeeComparisonLink,
    ViewAllPlansButton,
    ModalPriceCent,
    ZipBannerWrapper,
    ZipBannerContent,
    ZipBannerImageBox,
    ZipBannerImage,
    ZipBannerTextCol,
    ZipBannerTextGroup,
    ZipBannerSubText,
    ZipBannerHeadline,
    ZipBannerButton,
    ZipBannerButtonText,
    ChoosePlanTitleContainer
} from "../styles/components";

export const ComparisonContext = createContext(undefined);
export const ComparisonProvider = ComparisonContext.Provider;
export const useComparison = () => {
    const context = useContext(ComparisonContext);
    if (!context) throw new Error("useComparison must be used within ComparisonProvider");
    return context;
};


const ChoosePlanSectionTx = () => {
    const [activeTab, setActiveTab] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [showAllPlans, setShowAllPlans] = useState(false);
    const [cardTransforms, setCardTransforms] = useState<Record<number, { x: number; y: number }>>({});
    const [zipDialogOpen, setZipDialogOpen] = useState(false);
    const { plansLoading, plans, setZipCode, zipCode, setSelectedPlan, selectedPlan, utility, locationDenied, bestValuePlan, plansUnavailableReason } = usePlanContext();
    const navigate = useNavigate();// Move useNavigate to top level to avoid conditional hook call
    const location = useLocation();
    const routeBasedPromoCode = getCurrentPromoCode(location.pathname);
    const handleViewDetails = (plan: any) => { setSelectedPlan(plan); setModalOpen(true); };
    const handleCloseModal = () => { setModalOpen(false) };
    const handleViewAllPlans = () => {
        if (showAllPlans && activeTab === "all") {
            setShowAllPlans(false);
            setActiveTab("best");
            const pricingSection = document.getElementById("pricing-section");
            if (pricingSection) {
                const navbarHeight = 80;
                const elementPosition = pricingSection.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - navbarHeight;
                window.scrollTo({ top: offsetPosition, behavior: "smooth" });
            }
        } else {
            setShowAllPlans(true);
            setActiveTab("all");
        }
    };
    const handleScrollToComparison = async (plan: any) => {
        const comparisonSection = document.getElementById("comparison-section");
        if (comparisonSection) { 
            const navbarHeight = 80; // Adjust this value to match your sticky navbar height
            const elementPosition = comparisonSection.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - navbarHeight;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
        }
    };
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
        const card = e.currentTarget;
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
        const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
        setCardTransforms((prev) => ({ ...prev, [index]: { x: x * 20, y: y * 20 }, }));
    };
    const handleMouseLeave = (index: number) => {
        setCardTransforms((prev) => { const newTransforms = { ...prev }; newTransforms[index] = { x: 0, y: 0 }; return newTransforms; });
    };
    const handleZipCodeClick = () => { setZipDialogOpen(true); };
    const handleZipCodeClose = () => { setZipDialogOpen(false); };
    const handleZipCodeSave = (newZipCode: string) => { setZipCode(newZipCode); setZipDialogOpen(false); };

    // Clean up page_path from localStorage when component unmounts
    // useEffect(() => {
    //   return () => {
    //     localStorage.removeItem("page_path");
    //   };
    // }, []);

    // Show ZIP code banner if location access denied and no plans available for current ZIP
    if (locationDenied && !plansLoading && plans.length === 0 && plansUnavailableReason !== "api_error") {
        return <ZipCodeBanner />;
    }

    // Hide component if no ZIP code is set 
    if (!zipCode) { return null; }
    if (!plansLoading && plansUnavailableReason === "api_error") {
        return <PlansUnavailableFallback />;
    }
    // Handle empty plans array
    if (!plansLoading && plans.length === 0) {
        return (
            <ZipBannerWrapper>
                <ZipBannerContent>
                    <ZipBannerImageBox>
                        <ZipBannerImage
                            src="/images/constenewImg/locationBanner.png"
                            alt="Location Banner"
                        />
                    </ZipBannerImageBox>
                    <ZipBannerTextCol>
                        <ZipBannerTextGroup>
                            <ZipBannerSubText>
                                No plans available for {zipCode} yet
                            </ZipBannerSubText>
                            <ZipBannerHeadline>
                                Change your ZIP to see the best local plans.
                            </ZipBannerHeadline>
                        </ZipBannerTextGroup>
                        <ZipBannerButton variant="contained" onClick={handleZipCodeClick}>
                            <Box component="img" src="/images/constenewImg/tdesign_location-filled-white.svg" alt="Location" sx={{ width: "20px", height: "20px" }} />
                            <ZipBannerButtonText>
                                Change your ZIP
                            </ZipBannerButtonText>
                        </ZipBannerButton>
                        <ZipCodeDialog open={zipDialogOpen} zipCode={zipCode} onClose={handleZipCodeClose} onSave={handleZipCodeSave} />
                    </ZipBannerTextCol>
                </ZipBannerContent>
            </ZipBannerWrapper>
        );
    }
    // Sort plans by price (lowest to highest)
    const sortedPlans = [...plans].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    // Find the best value plan in sorted array
    // Find the lowest price 'A/C Protect Plus' plan
    const acProtectPlusPlans = sortedPlans.filter(p => p.plan_name && p.plan_name.toLowerCase().includes("protect plus"));
    const ACprotectPlan = acProtectPlusPlans.length > 0 ? acProtectPlusPlans[0] : null;
    let displayPlans = [];
    // First plan (if exists and not best value or ACprotect)
   if (sortedPlans.length > 0) {
        const firstPlan = sortedPlans.find(p => p.plan_id !== bestValuePlan?.plan_id && p.plan_id !== ACprotectPlan?.plan_id);
        if (firstPlan) displayPlans.push({
            ...firstPlan,
            highlight: false,
            badge: undefined,
             isComparsionlink: //check if the second plan is the 12 month plan and if its 12 month plan then show the comparison link otherwise dont show the comparison link
                    Number(firstPlan.contract_term) === 12 && 
                    firstPlan?.plan_name.toLowerCase().includes("12 month usage bill credit"),
        });
    }

    
    // Best value plan
    if (bestValuePlan) displayPlans.push({
        ...bestValuePlan,
            highlight: true,
            badge: "Best Value",
            isComparsionlink: //If BestValuePlan have Contract term 12 months AND Plan name must include "12 month usage bill credit"
                    Number(bestValuePlan.contract_term) === 12 && 
                    bestValuePlan?.plan_name.toLowerCase().includes("12 month usage bill credit"),
    });

 
    // A/C Protect Plus plan
    if (ACprotectPlan) displayPlans.push({
        ...ACprotectPlan,
        highlight: false,
        badge: undefined,
        isComparsionlink: false
    });

    // Fill with all other plans (no duplicates)
    for (let plan of sortedPlans) {
        if (!displayPlans.some(p => p.plan_id === plan.plan_id)) {
            displayPlans.push({
                ...plan,
                highlight: false,
                badge: undefined,
                isComparsionlink: false
            });
        }
    }
    // Filter plans based on activeTab (null means show all by default)
    const filteredPlans = activeTab === "best" ? displayPlans.slice(0, 3) : displayPlans;
    // show first 3 by default, all when showAllPlans is true
    const plansToDisplay = showAllPlans ? filteredPlans : filteredPlans.slice(0, 3);
    return (
        <ChoosePlanContainer>

            <PricingPlansWrapper id="pricing-section">
                {plansLoading ? (<PlansLoadingSkeleton />) :
                    (
                        <>

                            <ChoosePlanTitleContainer>
                                <ChoosePlanTitle>Choose a plan that works best for you</ChoosePlanTitle>
                            </ChoosePlanTitleContainer>

                            <PricingFiltersContainer>
                                <PricingFiltersWrapper>
                                    <TabButtonGroup>
                                    <TabButton variant="text"  active={activeTab === "best"|| activeTab === null} onClick={() => setActiveTab("best")}  >
                                            Our best plans
                                        </TabButton>
                                        <TabButton
                                            variant="text"
                                            active={activeTab === "all" }
                                            onClick={() => { setActiveTab("all"); setShowAllPlans(true); }}
                                        >
                                            All plans
                                        </TabButton>
                                      

                                    </TabButtonGroup>
                                    <FilterButton variant="text" onClick={handleZipCodeClick}>
                                        <Box component="img" src="/images/constenewImg/tdesign_location-filled.svg" alt="Location" sx={{ width: "20px", height: "20px" }} />
                                        Your ZIP: {zipCode}
                                        <EditIcon size={16} />
                                    </FilterButton>
                                </PricingFiltersWrapper>
                            </PricingFiltersContainer>
                            <PricingCardsGrid>
                                {plansToDisplay.map((plan, index) => {
                                    const transform = cardTransforms[index] ?? { x: 0, y: 0 };
                                    const hasTransform = Math.abs(transform.x) + Math.abs(transform.y) > 0;
                                    return (
                                        <PricingCard key={index} highlight={plan.highlight} hasTransform={hasTransform} transformX={transform.x} transformY={transform.y} onMouseMove={(e) => handleMouseMove(e, index)} onMouseLeave={() => handleMouseLeave(index)}>
                                            <PricingCardInner>
                                                <PricingCardHeader>
                                                    <PricingCardTitle>
                                                        <PlanName>{plan.plan_name}</PlanName>
                                                        {plan.badge && (
                                                            <BestValueBadge>
                                                                <BestValueText>Best Value</BestValueText>
                                                            </BestValueBadge>
                                                        )}
                                                    </PricingCardTitle>
                                                </PricingCardHeader>
                                                <PricingCardBody>
                                                    <PriceDisplay>
                                                        <PriceValue>{plan.price1000}</PriceValue>
                                                        <ModalPriceCent> ¢ </ModalPriceCent>
                                                        <PriceUnit>per kWh</PriceUnit>
                                                    </PriceDisplay>
                                                    <SignUpButton variant="contained" onClick={() => {
                                                        setSelectedPlan(plan);
                                                        localStorage.setItem("selectProduct", JSON.stringify(plan));
                                                        localStorage.setItem("utility", JSON.stringify(utility));
                                                        localStorage.setItem("promoCode", routeBasedPromoCode);
                                                        localStorage.setItem("page_path", window.location.pathname);
                                                        handleSignUp(plan, zipCode, utility, navigate, location)
                                                        // window.location.href = 'tel:(877) 352-3194';
                                                    }}>
                                                        {/* <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                                                                Call (877) 352-3194 to enroll
                                                            </Box>
                                                            <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>
                                                                Call to enroll
                                                            </Box> */}
                                                       Select the  Plan
                                                    </SignUpButton>
                                                    <FeaturesContainer>
                                                        {plan.features.map((feature, idx) => (
                                                            <FeatureItem key={idx}>
                                                                <CheckIcon size={16} />
                                                                <FeatureText>{feature}</FeatureText>
                                                            </FeatureItem>
                                                        ))}
                                                        <Box
                                                            sx={{ display: "flex", justifyContent: "flex-end" }}
                                                        >
                                                            <ViewDetailsLink
                                                                onClick={() => handleViewDetails(plan)}
                                                            >
                                                                View details
                                                            </ViewDetailsLink>
                                                        </Box>
                                                    </FeaturesContainer>
                                                    {
                                                        plan?.isComparsionlink && (
                                                             <SavingsContainer>
                                                        {plan.isComparsionlink ?
                                                            <SavingsBox highlight={plan.isComparsionlink}>

                                                                <SavingsLabel>Estimated Annual Savings</SavingsLabel>
                                                                <SavingsBottom>
                                                                    {plan?.savings ? <SavingsValue>{plan.savings}</SavingsValue> : null}
                                                                    <SeeComparisonLink onClick={() => { handleScrollToComparison(plan) }} >
                                                                        See comparison
                                                                    </SeeComparisonLink>
                                                                </SavingsBottom>


                                                            </SavingsBox>
                                                            : null}
                                                    </SavingsContainer>
                                                        )
                                                    }
                                                   
                                                </PricingCardBody>
                                            </PricingCardInner>
                                        </PricingCard>
                                    );
                                })}
                            </PricingCardsGrid>
                            <Box sx={{ display: "flex", padding: "12px", justifyContent: "center", alignItems: "center", gap: "10px", }} >
                                <ViewAllPlansButton onClick={handleViewAllPlans}>
                                    {showAllPlans && activeTab === "all" ? "Show Less" : "View All Plans"}
                                </ViewAllPlansButton>
                            </Box>
                            <PlanDetailsModal open={modalOpen} onClose={handleCloseModal} plan={selectedPlan} />
                            <ZipCodeDialog open={zipDialogOpen} zipCode={zipCode} onClose={handleZipCodeClose} onSave={handleZipCodeSave} />
                        </>
                    )}
            </PricingPlansWrapper>

        </ChoosePlanContainer>
    )
}

export default ChoosePlanSectionTx